import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import chevronDown from '@/assets/icons/chevron_down_stroke.svg?component';
import { Badge } from '@/components/atoms/Badge';
import { Button } from '@/components/atoms/Buttons';
import { SvgIcon } from '@/components/atoms/SvgIcon/SvgIcon';
import Tooltip from '@/components/atoms/Tooltip';
import {
    createDataTableColumnHelper,
    type DataTableColumnDef,
    timeSortFn,
} from '@/components/organisms/DataTable';
import type { ClientSummary } from '@/domain/client';
import { formatDate, formatDateTimeWorded } from '@/utils/date';

import { isInternalClient, isOpenEnded, type LicenseHealth, licenseHealth } from '../clientStatus';

import styles from './ClientTable.module.scss';

export const clientUrl = (clientId: number): string => `/internal/clients/${clientId}`;

const LICENSE_TONE = { active: 'success', expiring: 'warning', lapsed: 'danger' } as const;

/** Worst first, so sorting ascending brings the licenses that need a look to the top. */
const LICENSE_RANK: Record<LicenseHealth, number> = {
    lapsed: 0,
    expiring: 1,
    active: 2,
    none: 3,
};

// Every column but the name centres its header and values.
const CENTER = { align: 'center' } as const;

/** The clients table's columns. Search matches the id and the name only. */
export function useClientTableColumns(): DataTableColumnDef<ClientSummary>[] {
    const { t } = useTranslation();
    const columnHelper = createDataTableColumnHelper<ClientSummary>();

    const count = (id: string, header: string, value: (row: ClientSummary) => number) =>
        columnHelper.accessor(value, {
            id,
            header: t(header),
            enableGlobalFilter: false,
            meta: CENTER,
        });

    return columnHelper.columns([
        columnHelper.accessor((row) => row.id, {
            id: 'id',
            header: t('internal.clients.columns.id'),
            meta: CENTER,
        }),
        columnHelper.accessor((row) => row.name, {
            id: 'name',
            header: t('internal.clients.columns.name'),
            cell: ({ row }) => (
                <span className={styles.name}>
                    {/* The row takes the mouse; the link gives the keyboard a way in. */}
                    <Link
                        to={clientUrl(row.original.id)}
                        onClick={(event) => event.stopPropagation()}>
                        {row.original.name}
                    </Link>
                    {isInternalClient(row.original) && (
                        <Badge tone="internal" size="xs">
                            {t('internal.clients.status.internal')}
                        </Badge>
                    )}
                    {row.original.archived && (
                        <Badge tone="neutral" size="xs">
                            {t('internal.clients.status.archived')}
                        </Badge>
                    )}
                </span>
            ),
            sortFn: (a, b) => a.original.name.localeCompare(b.original.name),
        }),
        count('siteCount', 'internal.clients.columns.sites', (row) => row.siteCount),
        count('penCount', 'internal.clients.columns.pens', (row) => row.penCount),
        count('bioscopeCount', 'internal.clients.columns.bioscopes', (row) => row.bioscopeCount),
        columnHelper.accessor((row) => row.staleBioscopeCount, {
            id: 'staleBioscopeCount',
            header: t('internal.clients.columns.staleBioscopes'),
            enableGlobalFilter: false,
            meta: { ...CENTER, headerTitle: t('internal.clients.staleTooltip') },
            cell: ({ row }) => {
                const { staleBioscopeCount, bioscopeCount } = row.original;
                if (bioscopeCount === 0) return null;
                return (
                    <Badge tone={staleBioscopeCount > 0 ? 'warning' : 'success'} size="sm">
                        {t('internal.clients.staleOf', {
                            stale: staleBioscopeCount,
                            total: bioscopeCount,
                        })}
                    </Badge>
                );
            },
        }),
        columnHelper.accessor((row) => row.lastMeasurementAt, {
            id: 'lastMeasurement',
            header: t('internal.clients.columns.lastMeasurement'),
            meta: CENTER,
            enableGlobalFilter: false,
            cell: ({ row }) =>
                row.original.lastMeasurementAt
                    ? formatDateTimeWorded(row.original.lastMeasurementAt, 'minute')
                    : null,
            // A client that has never measured sorts last whichever way the column sorts.
            sortFn: timeSortFn,
        }),
        columnHelper.accessor((row) => row.contractCount, {
            id: 'contractCount',
            header: t('internal.clients.columns.contracts'),
            enableGlobalFilter: false,
            meta: CENTER,
            cell: ({ row }) => {
                const { contractCount, totalContractCount, contractedBioscopeCount } = row.original;
                if (totalContractCount === 0) return 0;
                const expanded = row.getIsExpanded();
                const toggle = row.getToggleExpandedHandler();
                const tooltip =
                    contractCount === 0
                        ? t('internal.clients.archivedContractsOnly')
                        : t('internal.clients.contractedBioscopes', {
                              count: contractedBioscopeCount,
                          });
                return (
                    <Tooltip content={tooltip} layout="inline">
                        {/* The row opens the client; this opens its contracts instead. */}
                        <Button
                            variant="link"
                            type="button"
                            aria-expanded={expanded}
                            aria-label={t('internal.clients.toggleContracts', {
                                name: row.original.name,
                            })}
                            onClick={(event) => {
                                event.stopPropagation();
                                toggle();
                            }}>
                            {contractCount}
                            <span className={expanded ? styles.chevronOpen : styles.chevron}>
                                <SvgIcon icon={chevronDown} size="custom" customSize={14} />
                            </span>
                        </Button>
                    </Tooltip>
                );
            },
        }),
        columnHelper.accessor((row) => licenseHealth(row), {
            id: 'licenseStatus',
            header: t('internal.clients.columns.licenseStatus'),
            enableGlobalFilter: false,
            meta: { ...CENTER, headerTitle: t('internal.clients.licenseTooltip') },
            cell: ({ row }) => {
                const health = licenseHealth(row.original);
                if (health === 'none') return null;
                const names = {
                    active: row.original.activeLicenses,
                    expiring: row.original.expiringLicenses,
                    lapsed: row.original.lapsedLicenses,
                }[health];
                const badge = (
                    <Badge tone={LICENSE_TONE[health]} size="sm">
                        {t(`internal.clients.license.${health}`, { count: names.length })}
                    </Badge>
                );
                // Active says it all; the other two name the license types behind the count.
                return health === 'active' ? (
                    badge
                ) : (
                    <Tooltip content={names.join(', ')} layout="inline">
                        {badge}
                    </Tooltip>
                );
            },
            sortFn: (a, b) =>
                LICENSE_RANK[licenseHealth(a.original)] - LICENSE_RANK[licenseHealth(b.original)],
        }),
        columnHelper.accessor((row) => row.nextLicenseExpiry, {
            id: 'nextLicenseExpiry',
            header: t('internal.clients.columns.nextLicenseExpiry'),
            enableGlobalFilter: false,
            meta: CENTER,
            cell: ({ row }) => {
                const expiry = row.original.nextLicenseExpiry;
                if (!expiry) return null;
                return isOpenEnded(expiry)
                    ? t('internal.clients.license.openEnded')
                    : formatDate(expiry, 'displayWeek');
            },
            sortFn: timeSortFn,
        }),
        count('userCount', 'internal.clients.columns.users', (row) => row.userCount),
        count('apiUserCount', 'internal.clients.columns.apiUsers', (row) => row.apiUserCount),
        columnHelper.accessor((row) => row.integrations.join(', '), {
            id: 'integrations',
            header: t('internal.clients.columns.integrations'),
            meta: CENTER,
            enableGlobalFilter: false,
        }),
        columnHelper.accessor((row) => row.createdAt, {
            id: 'createdAt',
            header: t('internal.clients.columns.createdAt'),
            meta: CENTER,
            enableGlobalFilter: false,
            cell: ({ row }) => formatDate(row.original.createdAt, 'displayWeek'),
            sortFn: timeSortFn,
        }),
    ]);
}
